import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileText, Calculator, ClipboardCheck, BookOpen, LogOut, Brain, CalendarDays, BarChart3, Flame, User } from 'lucide-react';
import PVSForms from './PVSForms';
import CalculatorApp from './Calculator';
import WSEDocumentViewer from './WSEDocumentViewer';
import LPGInventorySystem from './LPGInventorySystem';
import COPViewer from './COPViewer';
import Schedule from './Schedule';
import Analytics from './Analytics';
import PVSPageLayout from './PVSPageLayout';
import CalorGasHub from './CalorGasHub';
import Documents from './Documents';
import FloatingBackButton from './FloatingBackButton';
import Profile from './Profile';

const CALOR_PIN = '1993';

const ANALYTICS_EMAILS = ['gareth@pvslimited.com', 'contact@pvslimited.com', 'contact@pvslimited'];
const ANALYTICS_USERS = ['gareth', 'gareth_holton', 'contact'];

const DISPLAY_NAMES = {
  adybates: 'Ady',
  adybrunning: 'Ady',
  ethanbrunning: 'Ethan',
  gareth: 'Gareth',
  kevinwalker: 'Kevin',
  office: 'Carol',
  pvsengineer: 'Dave',
  shartley: 'Stephen',
  spencerwright: 'Spencer',
};

const welcomeFirstName = (fullName, username) => {
  const fromStored = String(fullName || '').trim().split(/\s+/).filter(Boolean)[0];
  if (fromStored) return fromStored;
  return DISPLAY_NAMES[String(username || '').toLowerCase()] || '';
};

const PVSHomePage = ({isAdmin: propIsAdmin, onLogout}) => {
  console.log('PVSHomePage component rendered');
  const location = useLocation();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('home');
  const [pendingFormKey, setPendingFormKey] = useState(null);
  const [pendingDocKey, setPendingDocKey] = useState(null);
  const [formSource, setFormSource] = useState(null);
  const [calorPinOpen, setCalorPinOpen] = useState(false);
  const [calorPinValue, setCalorPinValue] = useState('');
  const [calorPinError, setCalorPinError] = useState('');
  
  const [isAdmin, setIsAdmin] = useState(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        return user.isAdmin === true;
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
    return propIsAdmin || false;
  });

  const [userRole, setUserRole] = useState(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        return JSON.parse(userData).role || 'standard';
      }
    } catch (error) { /* ignore */ }
    return 'standard';
  });

  const [username, setUsername] = useState(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        return JSON.parse(userData).username || '';
      }
    } catch (error) { /* ignore */ }
    return '';
  });

  const [fullName, setFullName] = useState(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        return JSON.parse(userData).fullName || '';
      }
    } catch (error) { /* ignore */ }
    return '';
  });

  const [userEmail, setUserEmail] = useState(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        return JSON.parse(userData).email || '';
      }
    } catch (error) { /* ignore */ }
    return '';
  });
  
  useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        setIsAdmin(user.isAdmin === true);
        setUserRole(user.role || 'standard');
        setUsername(user.username || '');
        setFullName(user.fullName || '');
        setUserEmail(user.email || '');
      } else {
        setIsAdmin(false);
        setUserRole('standard');
        setUsername('');
        setFullName('');
        setUserEmail('');
      }
    } catch (error) {
      console.error('Error reading admin status:', error);
      setIsAdmin(false);
      setUserRole('standard');
      setUsername('');
      setFullName('');
      setUserEmail('');
    }
  }, []);
  
  useEffect(() => {
    if (location.state?.view) {
      console.log('Navigating to view from state:', location.state.view);
      setCurrentView(location.state.view);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
  };

  const welcomeName = welcomeFirstName(fullName, username);

  const openCalorPinModal = () => {
    setCalorPinValue('');
    setCalorPinError('');
    setCalorPinOpen(true);
  };

  const confirmCalorPin = () => {
    if (calorPinValue.trim() === CALOR_PIN) {
      setCalorPinOpen(false);
      setCurrentView('calor-gas');
    } else {
      setCalorPinError('Incorrect PIN');
    }
  };

  if (currentView === 'LPG Inventory') {
    return <LPGInventorySystem 
      onReturnToPVSHomePage={() => setCurrentView('home')} 
      isAdmin={isAdmin}
      userRole={userRole}
    />;
  }

  const handleNavigation = (path) => {
    console.log('Navigation triggered:', path);
    if (path === 'pvs-form') {
      setCurrentView('pvs-form');
    } else if (path === 'calculator') {
      setCurrentView('calculator');
    } else if (path === 'LPG Inventory') {
      setCurrentView('LPG Inventory');
    } else {
      setCurrentView(path);
    }
  };

  console.log('Current View:', currentView);

  const allButtons = [
    {
      title: 'Profile',
      icon: User,
      path: 'profile',
      description: 'Your holiday remaining and leave requests'
    },
    {
      title: 'Vessel Mind',
      icon: Brain,
      path: '/vesselmind',
      description: 'AI Assistant for Codes of Practice',
      isRoute: true,
      adminOnly: true
    },
    {
      title: 'Analytics',
      icon: BarChart3,
      path: 'analytics',
      description: 'Stock levels, lead times & performance',
      allowedUsers: ANALYTICS_USERS,
      allowedEmails: ANALYTICS_EMAILS
    },
    {
      title: 'Schedule',
      icon: CalendarDays,
      path: 'schedule',
      description: 'View and manage weekly work schedule'
    },
    {
      title: 'Forms',
      icon: ClipboardCheck,
      path: 'pvs-form',
      description: 'Access and manage PVS documentation'
    },
    {
      title: 'Calculator',
      icon: Calculator,
      path: 'calculator',
      description: 'LPG calculations and conversions'
    },
    {
      title: 'Vessel Inventory',
      icon: ClipboardCheck,
      path: 'LPG Inventory',
      description: 'View WSE documentation and guidelines'
    },
    {
      title: 'Documents',
      icon: FileText,
      path: 'documents',
      description: 'Access company documents'
    },
    {
      title: 'Codes of Practice',
      icon: BookOpen,
      path: 'codes-of-practice',
      description: 'Industry standards and regulatory guidance'
    },
    {
      title: 'Calor Gas',
      icon: Flame,
      path: 'calor-gas',
      description: 'Calor documents and contacts',
      allowedUsers: [
        'gareth',
        'gareth_holton',
        'ethanbrunning',
        'adybrunning',
        'kevinwalker',
        'shartley',
        'office',
      ],
      allowedEmails: ['contact@pvslimited.com'],
      calorGate: true
    }
  ];

  console.log('🔐 Admin Status:', isAdmin);
  const dashboardButtons = allButtons.filter(button => {
    if (button.allowedUsers || button.allowedEmails) {
      const normalizedUsername = (username || '').toLowerCase();
      const normalizedEmail = (userEmail || '').toLowerCase();
      const userAllowed = (button.allowedUsers || [])
        .map((u) => u.toLowerCase())
        .includes(normalizedUsername);
      const emailAllowed = (button.allowedEmails || [])
        .map((e) => e.toLowerCase())
        .includes(normalizedEmail);
      if (!userAllowed && !emailAllowed) {
        return false;
      }
    }
    if (button.emailOnly && (userEmail || '').toLowerCase() !== button.emailOnly.toLowerCase()) {
      return false;
    }
    if (button.userOnly && button.userOnly !== username) return false;
    if (button.adminOnly && !isAdmin && userRole !== 'readonly') return false;
    return true;
  });
  console.log('📊 Dashboard buttons:', dashboardButtons.length, 'visible');

  console.log('Rendering view:', currentView);

  if (currentView === 'profile') {
    return <Profile onReturnHome={() => setCurrentView('home')} />;
  }
  if (currentView === 'pvs-form') {
    const handleBackToProcessMap = () => {
      setPendingDocKey(formSource);
      setFormSource(null);
      setPendingFormKey(null);
      setCurrentView('documents');
    };
    return (
      <>
        <PVSForms
          initialFormKey={pendingFormKey}
          onInitialFormConsumed={() => setPendingFormKey(null)}
          onReturnToPVSHomePage={() => {
            setPendingFormKey(null);
            setFormSource(null);
            setCurrentView('home');
          }}
        />
        {formSource === 'VesselRefurbishmentProcessMap' && (
          <FloatingBackButton onClick={handleBackToProcessMap} label="Back to Process Map" />
        )}
      </>
    );
  }
  if (currentView === 'calculator') {
    return <CalculatorApp onReturnHome={() => setCurrentView('home')} />;
  }
  if (currentView === 'wse') {
    return <WSEDocumentViewer onReturnHome={() => setCurrentView('home')} />;
  }
  if (currentView === 'LPG Inventory') {
    return <LPGInventorySystem onReturnToPVSHomePage={() => setCurrentView('home')} isAdmin={isAdmin} userRole={userRole} />;
  }
  if (currentView === 'documents') {
    return (
      <Documents
        initialSelectedKey={pendingDocKey}
        onInitialSelectedKeyConsumed={() => setPendingDocKey(null)}
        onReturnHome={() => setCurrentView('home')}
        onNavigateToForm={(key, sourceKey) => {
          setPendingFormKey(key);
          setFormSource(sourceKey || null);
          setCurrentView('pvs-form');
        }}
      />
    );
  }
  if (currentView === 'codes-of-practice') {
    return <COPViewer onReturnHome={() => setCurrentView('home')} />;
  }
  if (currentView === 'schedule') {
    return <Schedule onReturnHome={() => setCurrentView('home')} />;
  }
  if (currentView === 'analytics') {
    return <Analytics onReturnHome={() => setCurrentView('home')} isAdmin={isAdmin} />;
  }
  if (currentView === 'calor-gas') {
    return (
      <CalorGasHub
        onReturnHome={() => setCurrentView('home')}
        onOpenTankCertification={() => {
          setPendingFormKey('tank-certification');
          setFormSource(null);
          setCurrentView('pvs-form');
        }}
      />
    );
  }

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex justify-between items-start mb-8">
          <h1 className="text-3xl font-bold text-white leading-tight">
            {welcomeName ? `Welcome Back, ${welcomeName}` : 'Welcome Back'}
          </h1>
          <button
            onClick={handleLogout}
            className="pvs-glass pvs-glass-hover flex items-center justify-center w-12 h-12"
            title="Logout"
          >
            <LogOut className="h-5 w-5" stroke="url(#icon-two-tone)" />
          </button>
        </div>

        <div className="grid gap-4">
          {dashboardButtons.map((button) => {
            const Icon = button.icon;
            return (
              <button
                key={button.path}
                className="w-full pvs-glass pvs-glass-hover flex items-center px-5 py-5 text-left"
                onClick={() => {
                  console.log('Button clicked:', button.path);
                  if (button.calorGate) {
                    openCalorPinModal();
                    return;
                  }
                  if (button.isRoute) {
                    navigate(button.path);
                  } else {
                    handleNavigation(button.path);
                  }
                }}
              >
                <div className="flex items-center space-x-4 w-full">
                  <div className="p-3 rounded-2xl flex-shrink-0"
                    style={{
                      background: 'rgba(74, 222, 128, 0.06)',
                      border: '1px solid rgba(74, 222, 128, 0.15)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    <Icon className="h-7 w-7" stroke="url(#icon-two-tone)" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {button.title}
                    </h3>
                    <p className="text-sm text-pvs-text-muted">
                      {button.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {calorPinOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="fixed inset-0 bg-black/70 border-0 cursor-default"
            aria-label="Close PIN dialog"
            onClick={() => setCalorPinOpen(false)}
          />
          <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-white mb-2">Enter PIN</h2>
            <p className="text-sm text-pvs-text-muted mb-4">Enter your Calor access PIN to continue.</p>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={calorPinValue}
              onChange={(e) => {
                setCalorPinValue(e.target.value.replace(/\D/g, ''));
                setCalorPinError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmCalorPin();
              }}
              className="pvs-input w-full mb-2"
              placeholder="PIN"
              autoFocus
            />
            {calorPinError ? <p className="text-sm text-red-400 mb-4">{calorPinError}</p> : <div className="mb-4" />}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-pvs-border text-white hover:bg-white/5"
                onClick={() => setCalorPinOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-pvs-accent text-pvs-bg font-semibold hover:bg-pvs-accent-dim"
                onClick={confirmCalorPin}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </PVSPageLayout>
  );
};

export default PVSHomePage;
